import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay'
import { APP_BASE_HREF } from '@angular/common'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  inject,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core'
import { MomentDateAdapter } from '@angular/material-moment-adapter'
import { MAT_BUTTON_CONFIG } from '@angular/material/button'
import { MatChipsModule } from '@angular/material/chips'
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatDialogModule } from '@angular/material/dialog'
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field'
import { MatMenuModule } from '@angular/material/menu'
import { MatSelectModule } from '@angular/material/select'
import { MatTimepickerModule } from '@angular/material/timepicker'
import { MatTooltipModule } from '@angular/material/tooltip'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { provideRouter, Router, RouteReuseStrategy, withComponentInputBinding } from '@angular/router'
import { GbDashboardBrandingDataAccessModule } from '@galabid/gb-dashboard/branding/data-access'
import { GbDashboardCoreFeatureModule } from '@galabid/gb-dashboard/core/feature'
import { UsersFacade } from '@galabid/gb-dashboard/user/data-access'
import { NgMapsCoreModule } from '@ng-maps/core'
import { GOOGLE_MAPS_API_CONFIG, NgMapsGoogleModule } from '@ng-maps/google'
import { NgMapsPlacesModule } from '@ng-maps/places'
import { EffectsModule } from '@ngrx/effects'
import { StoreModule } from '@ngrx/store'
import { StoreDevtoolsModule } from '@ngrx/store-devtools'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular'
import * as enUS from 'apps/gb-dashboard/src/assets/i18n/en-us.json'
import * as defaultLanguage from 'apps/gb-dashboard/src/assets/i18n/en.json'
import { EventTicketPurchasesFacade } from 'gb-dashboard/event-tickets/data-access-event-ticket-purchases'
import { GbDashboardAccountDataAccessModule } from 'libs/gb-dashboard/account/data-access/src/lib/gb-dashboard-account-data-access.module'
import { GbDashboardAccountFeatureAuthLoginModule } from 'libs/gb-dashboard/account/feature-auth/login/src'
import { GbDashboardAuthInterceptor } from 'libs/gb-dashboard/account/utils/src/lib/gb-dashboard-auth-interceptor/gb-dashboard-auth.interceptor'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import { GbDashboardAuctionsDataAccessSelectedAuctionModule } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/gb-dashboard-auctions-data-access-selected-auction.module'
import { GbDashboardAuctionsDataAccessModule } from 'libs/gb-dashboard/auctions/data-access/src/lib/gb-dashboard-auctions-data-access.module'
import { GbDashboardClientAccountDataAccessModule } from 'libs/gb-dashboard/client-account/data-access/src/lib/gb-dashboard-client-account-data-access.module'
import { EventTicketPackagesFacade } from 'libs/gb-dashboard/event-tickets/data-access-event-ticket-packages/src/lib/+state/event-ticket-packages.facade'
import { PresenterViewApiInterceptor } from 'libs/gb-dashboard/presenter/feature/src/lib/presenter-view-api-interceptor/presenter-view-api.interceptor'
import { GbDashboardSharedDataAccessCountrySpecModule } from 'libs/gb-dashboard/shared/data-access-country-spec/src/lib/gb-dashboard-shared-data-access-country-spec.module'
import { GbCustomDateAdapter } from 'libs/gb-dashboard/shared/ui/src/lib/gb-datetime-form-field/gb-custom-date-adapter'
import { GbDashboardDialogService, GbDashboardSharedUtilsModule } from 'libs/gb-dashboard/shared/utils/src'
import { GbCurrencyPipe } from 'libs/gb-dashboard/shared/utils/src/lib/gb-currency-pipe/gb-currency.pipe'
import { GbDashboardGoogleAnalyticsService } from 'libs/gb-dashboard/shared/utils/src/lib/gb-dashboard-google-analytics-service/gb-dashboard-google-analytics.service'
import { GbDatePipe } from 'libs/gb-dashboard/shared/utils/src/lib/gb-date-pipe/gb-date.pipe'
import { GbDashboardStaffFeatureShellModule } from 'libs/gb-dashboard/staff/feature-shell/src/lib/gb-dashboard-staff-feature-shell.module'
import { TablesApiService } from 'libs/gb-dashboard/tables/data-access/src/lib/+state/tables-api.service'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { appLanguageInitializerFactory } from 'libs/shared/initialization/src'
import { ErrorInterceptor } from 'libs/shared/utils/interceptors/src/lib/error.interceptor'
import { SharedUtilsLoaderModule } from 'libs/shared/utils/loader/src/lib/shared-utils-loader.module'
import { GbMicrosoftClarityService } from 'libs/shared/utils/services/src/lib/gb-microsoft-clarity/gb-microsoft-clarity.service'
import { environment } from '../environments/environment'
import { AppRoutingModule, routes } from './app-routing.module'
import { AppComponent } from './app.component'
import { appEffects, appMetaReducers, appReducers } from './core/app-core.state'
import { CustomRouteReuseStrategy } from './core/custom-route-reuse-strategy'
import { AUCTION_FACADE } from 'libs/shared/models/auction/auction.models'
import { EVENT_TICKET_PURCHASES_FACADE } from 'libs/shared/models/event-tickets/event-ticket-purchases.models'

export const MY_CUSTOM_DATE_FORMAT = {
  parse: {
    dateInput: 'MMM DD, YYYY', //Mar 03, 2025
    timeInput: 'hh:mm a',
  },
  display: {
    dateInput: 'MMM DD, YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'MMM DD, YYYY',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'hh:mm a',
    timeOptionLabel: 'hh:mm a',
  },
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot(),
    BrowserAnimationsModule,
    StoreModule.forRoot(appReducers, {
      metaReducers: appMetaReducers,
      runtimeChecks: {
        strictActionImmutability: true,
        strictStateImmutability: true,
      },
    }),
    EffectsModule.forRoot(appEffects),
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({
          name: 'gb-dashboard project Started',
          maxAge: 25, // Retains last 25 states
          logOnly: false, // Restrict extension to log-only mode
          autoPause: true, // Pauses recording actions and state changes when the extension window is not open
        }),
    MatDatepickerModule,
    MatMenuModule,
    MatDialogModule,
    MatSelectModule,
    MatTooltipModule,
    MatChipsModule,
    GbDashboardSharedUtilsModule,
    GbDashboardAuctionsDataAccessModule,
    GbDashboardAuctionsDataAccessSelectedAuctionModule,
    GbDashboardClientAccountDataAccessModule,
    GbDashboardSharedDataAccessCountrySpecModule,
    GbDashboardCoreFeatureModule,
    GbDashboardAccountFeatureAuthLoginModule,
    GbDashboardBrandingDataAccessModule,
    GbDashboardStaffFeatureShellModule,
    SharedUtilsLoaderModule,
    GbDashboardAccountDataAccessModule,
    MatTimepickerModule,
    NgMapsCoreModule,
    NgMapsGoogleModule,
    NgMapsPlacesModule.forRoot({
      autocomplete: {
        types: [], // empty to allow searching by business/venue names (establishments)
      },
    }),
  ],
  providers: [
    GbDatePipe,
    GbCurrencyPipe,
    provideAppInitializer(() => {
      const translate = inject(TranslateService)
      return appLanguageInitializerFactory(translate, defaultLanguage, enUS)()
    }),
    provideAppInitializer(() => inject(GbDashboardGoogleAnalyticsService).setupGoogleAnalytics()),
    provideAppInitializer(() => inject(GbMicrosoftClarityService).initClarity()),
    provideAppInitializer(() => {
      inject(Sentry.TraceService)
    }),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    { provide: Sentry.TraceService, deps: [Router] },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PresenterViewApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GbDashboardAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: APP_BASE_HREF,
      useValue: environment.appBaseHref,
    },
    { provide: APP_CONFIG, useValue: environment },
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    {
      provide: DateAdapter,
      useClass: GbCustomDateAdapter,
    },
    {
      provide: MomentDateAdapter,
      useExisting: DateAdapter,
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_CUSTOM_DATE_FORMAT },
    {
      // material design defaults configs
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic' },
    },
    { provide: MAT_BUTTON_CONFIG, useValue: { color: 'primary' } },
    // this is a workaround the new angular 21 popover default
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    {
      provide: GOOGLE_MAPS_API_CONFIG,
      useValue: {
        libraries: ['places'],
        apiKey: environment.googleMapsKey,
      },
    },
    // provide facades/services for shared lib injection
    { provide: 'TablesApiService', useClass: TablesApiService },
    { provide: 'AuctionFacade', useClass: SelectedAuctionFacade },
    { provide: AUCTION_FACADE, useClass: SelectedAuctionFacade },
    { provide: 'EventTicketPurchasesFacade', useClass: EventTicketPurchasesFacade },
    { provide: EVENT_TICKET_PURCHASES_FACADE, useClass: EventTicketPurchasesFacade },
    { provide: 'EventTicketPackagesFacade', useClass: EventTicketPackagesFacade },
    { provide: 'GbDialogService', useClass: GbDashboardDialogService },
    { provide: 'AllProfilesFacade', useClass: UsersFacade },
    // end shared lib facades/services
    provideRouter(routes, withComponentInputBinding()),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
